import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { catchError, filter, firstValueFrom, map, of, startWith } from 'rxjs';
import {
  NbAvatar,
  NbButton,
  NbCommandPalette,
  NbDrawer,
  NbIcon,
  NbIconButton,
  NbInternalStripe,
  NbSegmented,
  NbSideBrand,
  NbSideFoot,
  NbSideNavItem,
  NbSideNavSection,
  NbSidebar,
  NbToastHost,
  NbTopbar,
  ThemeService,
  initials,
  type CommandItem,
  type SegmentedOption,
} from '@nabotavle/ui';
import { AuthService, type MeResult } from '@nabotavle/auth';
import {
  TenantAdminService,
  type AuditEntry,
  type EmailDelivery,
  type PagedResult,
  type SmsDelivery,
  type TenantSummary,
} from '@nabotavle/data-access';
import { LocaleService, type Locale } from '@nabotavle/util';
import { ArrowRight, Bell, Building2, LogOut, Mail, Moon, Plus, Sun } from 'lucide';
import type { AdminAlert, VarslerDrawerData } from './shell/varsler-drawer';

interface NavLink {
  /** i18n key (`shell.nav*`). */
  readonly label: string;
  readonly path: string;
  /** Extra route prefixes that keep this item active (`/lag/*` and `/import/*` belong to Portefølje). */
  readonly also?: readonly string[];
  /** Shows the audit-deviation badge. */
  readonly badge?: boolean;
}
interface NavGroup {
  /** i18n key (`shell.group*`). */
  readonly label: string;
  readonly items: readonly NavLink[];
}

/** Plan § 5.1: Plattform · Logger · Konto. */
const NAV_GROUPS: readonly NavGroup[] = [
  { label: 'shell.groupPlatform', items: [
    { label: 'shell.navPortefolje', path: '/portefolje', also: ['/lag', '/import'] },
    { label: 'shell.navOpprett', path: '/opprett' },
    { label: 'shell.navDrift', path: '/drift', badge: true },
  ] },
  { label: 'shell.groupLogs', items: [
    { label: 'shell.navRevisjon', path: '/revisjonslogg' },
    { label: 'shell.navEpost', path: '/epostlogg' },
    { label: 'shell.navMaler', path: '/epost-maler' },
    { label: 'shell.navBrevo', path: '/brevo' },
    { label: 'shell.navSms', path: '/sms-logg' },
  ] },
  { label: 'shell.groupAccount', items: [{ label: 'shell.navKonto', path: '/konto' }] },
];

const SEARCH_DEBOUNCE_MS = 200;
const SEARCH_MIN_CHARS = 2;
const SEARCH_MAX_HITS = 5;
/** Max deviations in the bell drawer. */
const ALERTS_MAX = 20;
/** localStorage key for «Merk alle som lest» (a timestamp — no API, plan § 5.3). */
const SEEN_KEY = 'nb.adminkonsoll.varslerSeen';
const EMAIL_FAILED = new Set(['failed', 'bounced', 'blocked']);
const SMS_FAILED = new Set(['failed', 'undelivered', 'capped']);

/**
 * The admin shell (2026-09 admin prototype, plan § 5): dark `admin` sidebar with three groups,
 * internal-tool stripe, ⌘K topbar, a bell that opens the deviations drawer, toast host and the
 * light/dark theme switch. Chrome is hidden on /login. Every string is an i18n key; strings
 * built in TS go through `tr()` inside a `computed` so a locale switch re-renders them.
 */
@Component({
  selector: 'admin-root',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    TranslatePipe,
    NbInternalStripe,
    NbSidebar,
    NbSideBrand,
    NbSideNavSection,
    NbSideNavItem,
    NbSideFoot,
    NbTopbar,
    NbAvatar,
    NbButton,
    NbIconButton,
    NbIcon,
    NbSegmented,
    NbCommandPalette,
    NbToastHost,
  ],
  templateUrl: './app.html',
  styleUrl: './app.css',
})
export class App {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly admin = inject(TenantAdminService);
  private readonly drawer = inject(NbDrawer);
  private readonly theme = inject(ThemeService);
  private readonly t = inject(TranslateService);
  protected readonly locale = inject(LocaleService);

  protected readonly Bell = Bell;
  protected readonly LogOut = LogOut;
  protected readonly Plus = Plus;
  protected readonly navGroups = NAV_GROUPS;

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );
  // Pre-login routes (login, glemt passord, nytt passord) render without sidebar/topbar.
  protected readonly showChrome = computed(() => !/^\/(login|glemt-passord|nytt-passord)(?=[/?#]|$)/.test(this.url()));

  // Gated on auth: the root component also lives on /login, where an eager fetch would 401.
  private readonly me = httpResource<MeResult>(() => (this.auth.isAuthenticated() ? '/api/auth/me' : undefined));
  /** Badge + drawer source. Reloaded on every navigation so a just-performed action shows up. */
  private readonly audit = httpResource<AuditEntry[]>(() =>
    this.auth.isAuthenticated() ? { url: '/api/admin/audit', params: { limit: '100' } } : undefined,
  );
  private readonly emails = httpResource<EmailDelivery[]>(() =>
    this.auth.isAuthenticated() ? { url: '/api/admin/epost', params: { limit: '50' } } : undefined,
  );
  private readonly smses = httpResource<SmsDelivery[]>(() =>
    this.auth.isAuthenticated() ? { url: '/api/admin/sms', params: { limit: '50' } } : undefined,
  );

  protected readonly userName = computed(() => (this.me.hasValue() ? this.me.value().name : '') || this.tr('shell.userFallback'));
  protected readonly userInitials = computed(() => initials(this.userName()));

  private readonly auditProblems = computed(() => (this.audit.hasValue() ? this.audit.value() : []).filter((a) => a.outcome !== 'success'));
  /** Sidebar badge on «Drift & innsikt» — hidden when 0. */
  protected readonly deviations = computed(() => this.auditProblems().length || null);

  /** The bell drawer's list: audit deviations + failed e-post/SMS, newest first, max 20 (plan § 5.3). */
  private readonly alerts = computed<AdminAlert[]>(() => {
    const items: AdminAlert[] = [
      ...this.auditProblems().map((a): AdminAlert => ({
        id: `audit:${a.ts}:${a.action}`, kind: 'audit', atUtc: a.ts,
        title: this.tr('skuff.varsler.auditTitle', { action: a.action }),
        sub: this.tr('skuff.varsler.auditSub', { outcome: a.outcome, target: a.targetId ?? a.accessedTenantId ?? '—' }),
      })),
      ...(this.emails.hasValue() ? this.emails.value() : []).filter((e) => EMAIL_FAILED.has(e.status)).map((e): AdminAlert => ({
        id: `email:${e.id}`, kind: 'email', atUtc: e.updatedAtUtc,
        title: this.tr('skuff.varsler.emailTitle', { status: e.status }),
        sub: e.error || e.subject,
      })),
      ...(this.smses.hasValue() ? this.smses.value() : []).filter((s) => SMS_FAILED.has(s.status)).map((s): AdminAlert => ({
        id: `sms:${s.id}`, kind: 'sms', atUtc: s.updatedAtUtc,
        title: this.tr('skuff.varsler.smsTitle', { status: s.status }),
        sub: s.error || s.recipient,
      })),
    ];
    return items.sort((a, b) => b.atUtc.localeCompare(a.atUtc)).slice(0, ALERTS_MAX);
  });
  private readonly seenAt = signal(this.readSeen());
  /** Unread = newer than the local «read» stamp; feeds the bell dot. */
  protected readonly unread = computed(() => this.alerts().filter((a) => a.atUtc > this.seenAt()).length);

  protected readonly localeOptions: SegmentedOption[] = [
    { value: 'nb', label: 'Bokmål' },
    { value: 'nn', label: 'Nynorsk' },
    { value: 'en', label: 'English' },
  ];

  protected readonly cmdOpen = signal(false);
  private readonly cmdQuery = signal('');
  protected readonly searching = signal(false);
  private readonly tenantHits = signal<readonly { id: string; name: string; sub: string }[]>([]);
  private searchTimer: ReturnType<typeof setTimeout> | undefined;

  /** ⌘K: Handling · Lag (API search) · Side (plan § 5.3). «Verifiser kjeden» waits for the audit/verify endpoint (A5, D7). */
  protected readonly cmdItems = computed<CommandItem[]>(() => {
    const side = this.tr('palett.kindSide');
    const act = this.tr('palett.kindHandling');
    const lag = this.tr('palett.kindLag');
    return [
      { id: 'act:opprett', label: this.tr('palett.opprett'), sub: this.tr('palett.opprettSub'), kind: act, icon: Plus },
      { id: 'act:testmail', label: this.tr('palett.testmail'), sub: this.tr('palett.testmailSub'), kind: act, icon: Mail },
      { id: 'act:tema', label: this.tr(this.theme.isDark() ? 'palett.temaLyst' : 'palett.temaMorkt'), sub: this.tr('palett.temaSub'), kind: act, icon: this.theme.isDark() ? Sun : Moon },
      ...this.tenantHits().map((h) => ({ id: `lag:${h.id}`, label: h.name, sub: h.sub, kind: lag, icon: Building2, keywords: this.cmdQuery() })),
      ...this.navGroups.flatMap((g) => g.items.map((i) => ({
        id: `page:${i.path}`, label: this.tr(i.label), sub: this.tr('palett.goTo', { group: this.tr(g.label).toLowerCase() }), kind: side, icon: ArrowRight,
      }))),
    ];
  });
  protected readonly cmdEmptyText = computed(() =>
    this.tr(this.cmdQuery().length < SEARCH_MIN_CHARS ? 'palett.searchHint' : 'palett.empty'),
  );

  constructor() {
    inject(DestroyRef).onDestroy(() => clearTimeout(this.searchTimer));
    // Reload the badge/drawer sources after each navigation (an action is always followed by one).
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
      if (!this.auth.isAuthenticated()) return;
      this.audit.reload();
      this.emails.reload();
      this.smses.reload();
    });
  }

  protected setLocale(value: string | undefined): void {
    if (value) this.locale.use(value as Locale);
  }

  protected onCmdQuery(raw: string): void {
    const q = raw.trim();
    this.cmdQuery.set(q);
    clearTimeout(this.searchTimer);
    if (q.length < SEARCH_MIN_CHARS) {
      this.tenantHits.set([]);
      this.searching.set(false);
      return;
    }
    this.searchTimer = setTimeout(() => void this.searchTenants(q), SEARCH_DEBOUNCE_MS);
  }

  private async searchTenants(q: string): Promise<void> {
    this.searching.set(true);
    const empty: PagedResult<TenantSummary> = { items: [], total: 0, page: 1, pageSize: SEARCH_MAX_HITS };
    const page = await firstValueFrom(this.admin.listTenants({ search: q, pageSize: SEARCH_MAX_HITS }).pipe(catchError(() => of(empty))));
    if (this.cmdQuery() !== q) return; // stale
    this.tenantHits.set(page.items.slice(0, SEARCH_MAX_HITS).map((t) => ({
      id: t.id, name: t.name, sub: this.tr('palett.lagSub', { type: t.type, status: t.status, units: t.unitCount }),
    })));
    this.searching.set(false);
  }

  protected runCmd(item: CommandItem): void {
    if (item.id.startsWith('page:')) return this.go(item.id.slice(5));
    if (item.id.startsWith('lag:')) return this.go(`/lag/${encodeURIComponent(item.id.slice(4))}`);
    switch (item.id) {
      case 'act:opprett': return this.go('/opprett');
      case 'act:testmail': return this.go('/epostlogg');
      case 'act:tema': return this.theme.toggleDark();
    }
  }

  /** The drawer is lazy — it (and its empty state) stays out of the initial bundle. */
  protected async openVarsler(): Promise<void> {
    const { VarslerDrawer } = await import('./shell/varsler-drawer');
    const data: VarslerDrawerData = { items: this.alerts(), markAll: () => this.markAllSeen() };
    this.drawer.open(VarslerDrawer, { data, size: 'lg', ariaLabel: this.t.instant('skuff.varsler.title') });
  }

  private markAllSeen(): void {
    const now = new Date().toISOString();
    this.seenAt.set(now);
    try { localStorage.setItem(SEEN_KEY, now); } catch { /* storage blocked — the stamp lives for this session only */ }
  }
  private readSeen(): string {
    try { return localStorage.getItem(SEEN_KEY) ?? ''; } catch { return ''; }
  }

  /** `instant()` that also tracks the active locale, so enclosing `computed`s re-evaluate on a switch. */
  private tr(key: string, params?: Record<string, unknown>): string {
    this.t.currentLang();
    return this.t.instant(key, params);
  }

  protected isActive(item: NavLink): boolean {
    const u = this.url();
    return [item.path, ...(item.also ?? [])].some((p) => u === p || u.startsWith(p + '/') || u.startsWith(p + '?'));
  }
  protected go(path: string): void {
    void this.router.navigateByUrl(path);
  }
  protected logout(): void {
    this.auth.logout();
    void this.router.navigateByUrl('/login');
  }
}
